import Phaser from "phaser";
import { TOWERS } from "../config/towers";
import { ENEMIES } from "../config/enemies";

const PALETTE = [
  0x315e86, 0xa94f2c, 0x704e9c, 0x555c61, 0x7a5f39, 0x4f7864, 0x6d8fa8, 0xb35a32, 0x557a42,
  0x806847, 0x725b97, 0x7d6b4e, 0x9c8238, 0x446d72, 0x536478, 0x824b4b, 0x63564a, 0x6d4b8a,
  0xb38b32, 0x9e4935,
];
const TOWER_COLORS: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(Object.keys(TOWERS).map((id, i) => [id, PALETTE[i % PALETTE.length]!])),
);
const ENEMY_COLORS: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(Object.values(ENEMIES).map((e) => [e.id, e.visual.accentColor])),
);

function createArcherTower(scene: Phaser.Scene, level: number): void {
  const g = scene.add.graphics();
  const stone = level >= 5 ? 0xc9c0aa : level >= 3 ? 0xa99f8a : 0x8f806b;
  const stoneDark = 0x4b4339;
  const timber = level >= 2 ? 0x6f4b2d : 0x7b5432;
  const timberDark = 0x3f2a1b;
  const blue = level >= 4 ? 0x2f5fa7 : 0x3c689e;
  const blueDark = 0x203f68;
  const gold = level >= 4 ? 0xe0bd58 : 0xc59f43;

  g.fillStyle(0x0a0908, 0.4);
  g.fillEllipse(64, 106, 104, 24);

  if (level <= 2) {
    g.fillStyle(timberDark);
    g.fillRoundedRect(28, 48, 72, 50, 5);
    g.fillStyle(timber);
    g.fillRoundedRect(32, 43, 64, 50, 4);
    g.lineStyle(4, timberDark, 1);
    g.lineBetween(38, 45, 38, 93);
    g.lineBetween(90, 45, 90, 93);
    g.lineBetween(32, 66, 96, 66);
    g.lineBetween(34, 90, 94, 46);
    g.lineBetween(34, 46, 94, 90);
  } else {
    g.fillStyle(stoneDark);
    g.fillRoundedRect(24, 45, 80, 55, 7);
    g.fillStyle(stone);
    g.fillRoundedRect(28, 40, 72, 56, 6);
    g.lineStyle(2, stoneDark, 0.45);
    for (let y = 51; y <= 88; y += 13) {
      g.lineBetween(30, y, 98, y);
      const offset = ((y - 51) / 13) % 2 === 0 ? 0 : 11;
      for (let x = 31 + offset; x < 98; x += 22) g.lineBetween(x, y - 10, x, y);
    }
    g.fillStyle(stoneDark);
    g.fillRect(20, 58, 10, 40);
    g.fillRect(98, 58, 10, 40);
  }

  if (level >= 2) {
    g.fillStyle(level >= 3 ? stone : timber);
    g.fillRoundedRect(24, 31, 80, 23, 4);
    g.lineStyle(3, stoneDark, 0.9);
    g.strokeRoundedRect(24, 31, 80, 23, 4);
    for (let x = 27; x <= 91; x += 21) {
      g.fillStyle(level >= 3 ? stone : timber);
      g.fillRect(x, 24, 12, 13);
    }
  }

  if (level >= 4) {
    g.fillStyle(stone);
    g.fillRoundedRect(12, 52, 18, 47, 5);
    g.fillRoundedRect(98, 52, 18, 47, 5);
    g.fillStyle(blue);
    g.fillTriangle(8, 54, 21, 30, 34, 54);
    g.fillTriangle(94, 54, 107, 30, 120, 54);
    g.lineStyle(3, blueDark, 1);
    g.strokeTriangle(8, 54, 21, 30, 34, 54);
    g.strokeTriangle(94, 54, 107, 30, 120, 54);
  }

  g.fillStyle(blue);
  g.fillTriangle(24, 44, 64, 7, 104, 44);
  g.lineStyle(4, blueDark, 1);
  g.strokeTriangle(24, 44, 64, 7, 104, 44);

  g.fillStyle(0x6a4328);
  g.fillCircle(64, 31, level >= 4 ? 8 : 7);
  g.fillStyle(0xd9b28a);
  g.fillCircle(64, 27, level >= 4 ? 6 : 5);
  g.fillStyle(blueDark);
  g.fillTriangle(58, 25, 64, 16, 70, 25);
  g.fillStyle(0x4b321f);
  g.fillRect(61, 33, 6, 11);

  g.lineStyle(level >= 4 ? 4 : 3, 0xe8ce88, 1);
  g.beginPath();
  g.moveTo(49, 25);
  g.lineTo(45, 31);
  g.lineTo(49, 37);
  g.strokePath();
  g.lineStyle(2, 0x4b2d1e, 1);
  g.lineBetween(45, 31, 72, 31);
  g.fillStyle(0xc9c5b8);
  g.fillTriangle(72, 27, 80, 31, 72, 35);

  g.fillStyle(0x5b3822);
  g.fillRoundedRect(75, 34, 13, 22, 3);
  for (let x = 78; x <= 86; x += 4) {
    g.lineStyle(2, 0xd9c59b, 1);
    g.lineBetween(x, 34, x - 4, 23);
  }

  if (level >= 3) {
    g.fillStyle(blue);
    g.fillTriangle(24, 58, 42, 62, 24, 80);
    g.fillStyle(gold);
    g.fillCircle(29, 65, 3);
    g.fillCircle(99, 65, 4);
  }

  if (level >= 5) {
    g.fillStyle(gold, 0.16);
    g.fillCircle(64, 28, 31);
    g.lineStyle(3, gold, 0.82);
    g.strokeCircle(64, 28, 27);
    g.fillStyle(0x6b3d24);
    g.fillRoundedRect(40, 5, 48, 9, 4);
    g.fillStyle(gold);
    g.fillCircle(40, 9, 4);
    g.fillCircle(88, 9, 4);
    g.lineStyle(3, 0x49301f, 1);
    g.lineBetween(46, 9, 82, 9);
    g.lineStyle(2, 0xd9c59b, 1);
    g.lineBetween(46, 5, 82, 13);
    g.lineBetween(46, 13, 82, 5);
    for (let i = 0; i < 6; i += 1) {
      const angle = (Math.PI * 2 * i) / 6;
      g.lineStyle(2, gold, 0.75);
      g.lineBetween(
        64 + Math.cos(angle) * 29,
        28 + Math.sin(angle) * 29,
        64 + Math.cos(angle) * 35,
        28 + Math.sin(angle) * 35,
      );
    }
  }

  g.fillStyle(stoneDark);
  g.fillRoundedRect(55, 76, 18, 22, 8);
  g.fillStyle(0xe4b955, 0.5);
  g.fillRoundedRect(59, 80, 10, 14, 5);

  g.generateTexture(`tower-archer-l${level}`, 128, 116);
  if (level === 1) g.generateTexture("tower-archer", 128, 116);
  g.destroy();
}

type ProductionTowerFamily = "artillery" | "arcane" | "defensive" | "elite";

interface ProductionTowerStyle {
  family: ProductionTowerFamily;
  primary: number;
  secondary: number;
  accent: number;
}

const PRODUCTION_TOWER_STYLES: Readonly<Record<string, ProductionTowerStyle>> = Object.freeze({
  rocket: { family: "artillery", primary: 0x8d4b2d, secondary: 0x3a312a, accent: 0xf0a13a },
  cannon: { family: "artillery", primary: 0x4a5258, secondary: 0x272b2e, accent: 0xd6b45a },
  ballista: { family: "artillery", primary: 0x7a532f, secondary: 0x3f2b1c, accent: 0xd7c07a },
  bombard: { family: "artillery", primary: 0x6b4634, secondary: 0x2d2521, accent: 0xc96b37 },
  lightning: { family: "arcane", primary: 0x6e58a8, secondary: 0x30294b, accent: 0xf2dd70 },
  frost: { family: "arcane", primary: 0x77b4cf, secondary: 0x2d5367, accent: 0xdaf5ff },
  flame: { family: "arcane", primary: 0xb64a2e, secondary: 0x4b2620, accent: 0xffc44f },
  poison: { family: "arcane", primary: 0x59783f, secondary: 0x293a22, accent: 0xb8df62 },
  crossbow: { family: "defensive", primary: 0x5d4938, secondary: 0x2f261f, accent: 0xc9b070 },
  "scout-tower": { family: "defensive", primary: 0x3f684e, secondary: 0x203b2c, accent: 0xe0c56e },
  sniper: { family: "defensive", primary: 0x394d5b, secondary: 0x202a31, accent: 0xc8d4da },
  execution: { family: "defensive", primary: 0x71353a, secondary: 0x351d20, accent: 0xd1a85a },
  earthshaker: { family: "elite", primary: 0x7a5738, secondary: 0x3c2d24, accent: 0xe19b49 },
  time: { family: "elite", primary: 0x536f87, secondary: 0x293847, accent: 0x8fe7ee },
  alchemist: { family: "elite", primary: 0x678046, secondary: 0x334126, accent: 0xd8e871 },
  banner: { family: "elite", primary: 0x365f96, secondary: 0x243a58, accent: 0xf0c860 },
  arcane: { family: "elite", primary: 0x7655a3, secondary: 0x33264b, accent: 0xe4c8ff },
  gold: { family: "elite", primary: 0xa77a2e, secondary: 0x59421f, accent: 0xffdc70 },
  dragon: { family: "elite", primary: 0x9b3b31, secondary: 0x4a211f, accent: 0xffa451 },
});

function drawProductionBase(
  g: Phaser.GameObjects.Graphics,
  level: number,
  style: ProductionTowerStyle,
): void {
  const stone = level >= 5 ? 0xc8c0ae : level >= 3 ? 0xa79d8b : 0x8d806f;
  const stoneDark = level >= 4 ? 0x484640 : 0x51483f;
  const gold = level >= 4 ? 0xe2bf5b : 0xb99343;

  g.fillStyle(0x090807, 0.42);
  g.fillEllipse(64, 106, 104, 24);

  g.fillStyle(stoneDark);
  g.fillRoundedRect(24, 48, 80, 50, 7);
  g.fillStyle(stone);
  g.fillRoundedRect(28, 43, 72, 51, 6);
  g.lineStyle(2, stoneDark, 0.5);
  for (let y = 55; y <= 88; y += 12) {
    g.lineBetween(30, y, 98, y);
    const offset = ((y - 55) / 12) % 2 === 0 ? 0 : 10;
    for (let x = 32 + offset; x < 98; x += 20) g.lineBetween(x, y - 9, x, y);
  }

  if (level >= 2) {
    g.fillStyle(stone);
    g.fillRoundedRect(21, 35, 86, 20, 5);
    g.lineStyle(3, stoneDark, 1);
    g.strokeRoundedRect(21, 35, 86, 20, 5);
    for (let x = 23; x <= 91; x += 22) g.fillRect(x, 27, 13, 14);
  }

  if (level >= 3) {
    g.fillStyle(stoneDark);
    g.fillRect(17, 57, 13, 40);
    g.fillRect(98, 57, 13, 40);
    g.fillStyle(style.primary);
    g.fillTriangle(16, 60, 29, 64, 16, 84);
    g.fillTriangle(112, 60, 99, 64, 112, 84);
    g.fillStyle(gold);
    g.fillCircle(22, 67, 3);
    g.fillCircle(106, 67, 3);
  }

  if (level >= 4) {
    g.fillStyle(stone);
    g.fillRoundedRect(10, 52, 18, 46, 5);
    g.fillRoundedRect(100, 52, 18, 46, 5);
    g.fillStyle(style.secondary);
    g.fillTriangle(7, 54, 19, 33, 31, 54);
    g.fillTriangle(97, 54, 109, 33, 121, 54);
    g.lineStyle(3, stoneDark, 1);
    g.strokeTriangle(7, 54, 19, 33, 31, 54);
    g.strokeTriangle(97, 54, 109, 33, 121, 54);
  }

  if (level >= 5) {
    g.fillStyle(style.accent, 0.16);
    g.fillCircle(64, 31, 34);
    g.lineStyle(3, style.accent, 0.8);
    g.strokeCircle(64, 31, 29);
    for (let i = 0; i < 8; i += 1) {
      const angle = (Math.PI * 2 * i) / 8;
      g.lineBetween(
        64 + Math.cos(angle) * 31,
        31 + Math.sin(angle) * 31,
        64 + Math.cos(angle) * 37,
        31 + Math.sin(angle) * 37,
      );
    }
  }

  g.fillStyle(stoneDark);
  g.fillRoundedRect(55, 75, 18, 22, 8);
  g.fillStyle(gold, 0.55);
  g.fillRoundedRect(59, 79, 10, 14, 5);
}

function drawArtilleryWeapon(
  g: Phaser.GameObjects.Graphics,
  id: string,
  level: number,
  style: ProductionTowerStyle,
): void {
  if (id === "rocket") {
    const tubes = level >= 4 ? 4 : level >= 2 ? 3 : 2;
    for (let i = 0; i < tubes; i += 1) {
      const x = 42 + i * 15 - (tubes - 2) * 7;
      g.fillStyle(style.secondary);
      g.fillRoundedRect(x, 8, 11, 36, 5);
      g.fillStyle(style.accent);
      g.fillTriangle(x, 9, x + 5.5, -1, x + 11, 9);
      g.fillStyle(0xd9c379);
      g.fillRect(x + 3, 19, 5, 11);
    }
    if (level >= 5) {
      g.fillStyle(0x7d2d24);
      g.fillRoundedRect(39, 1, 50, 9, 4);
      g.lineStyle(3, style.accent, 0.9);
      g.strokeRoundedRect(39, 1, 50, 9, 4);
    }
  } else if (id === "cannon") {
    const barrelWidth = level >= 4 ? 38 : 30;
    g.fillStyle(style.secondary);
    g.fillRoundedRect(64 - barrelWidth / 2, 10, barrelWidth, 35, 7);
    g.fillStyle(style.primary);
    g.fillRoundedRect(64 - barrelWidth / 2 + 4, 7, barrelWidth - 8, 10, 5);
    g.fillStyle(0x111315);
    g.fillCircle(64, 9, level >= 4 ? 11 : 9);
    g.fillStyle(style.accent);
    g.fillRect(48, 36, 32, 8);
    g.fillCircle(45, 43, level >= 3 ? 8 : 6);
    g.fillCircle(83, 43, level >= 3 ? 8 : 6);
  } else if (id === "ballista") {
    g.fillStyle(style.primary);
    g.fillRoundedRect(46, 27, 36, 13, 4);
    g.fillRect(60, 4, 8, 42);
    g.lineStyle(level >= 4 ? 5 : 4, style.accent, 1);
    g.lineBetween(31, 10, 64, 28);
    g.lineBetween(97, 10, 64, 28);
    g.lineStyle(2, style.secondary, 1);
    g.lineBetween(31, 10, 31, 42);
    g.lineBetween(97, 10, 97, 42);
    g.lineBetween(31, 42, 64, 28);
    g.lineBetween(97, 42, 64, 28);
    g.fillStyle(0xd9d5c5);
    g.fillTriangle(64, -2, 58, 8, 70, 8);
  } else {
    g.fillStyle(style.secondary);
    g.fillCircle(64, 26, level >= 4 ? 22 : 18);
    g.fillStyle(0x151313);
    g.fillCircle(64, 18, level >= 4 ? 13 : 10);
    g.fillStyle(style.primary);
    g.fillRoundedRect(44, 34, 40, 12, 5);
    g.fillStyle(style.accent);
    g.fillCircle(48, 43, 6);
    g.fillCircle(80, 43, 6);
    if (level >= 3) {
      g.lineStyle(4, style.secondary, 1);
      g.lineBetween(64, 20, 86, 4);
    }
  }
}

function drawArcaneFocus(
  g: Phaser.GameObjects.Graphics,
  id: string,
  level: number,
  style: ProductionTowerStyle,
): void {
  const radius = level >= 4 ? 20 : 16;
  g.fillStyle(style.secondary);
  g.fillRoundedRect(54, 25, 20, 22, 5);
  g.lineStyle(4, style.primary, 1);
  g.lineBetween(64, 45, 64, 8);
  g.fillStyle(style.primary, 0.35);
  g.fillCircle(64, 19, radius + 6);
  g.fillStyle(style.primary, 0.9);
  g.fillCircle(64, 19, radius);
  g.lineStyle(3, style.accent, 0.9);
  g.strokeCircle(64, 19, radius + 3);

  if (id === "lightning") {
    g.lineStyle(level >= 4 ? 5 : 4, style.accent, 1);
    g.lineBetween(69, -2, 55, 17);
    g.lineBetween(55, 17, 73, 15);
    g.lineBetween(73, 15, 59, 38);
  } else if (id === "frost") {
    g.lineStyle(3, style.accent, 1);
    g.lineBetween(64, -2, 64, 40);
    g.lineBetween(45, 19, 83, 19);
    g.lineBetween(51, 6, 77, 32);
    g.lineBetween(77, 6, 51, 32);
    g.fillStyle(style.accent, 0.85);
    g.fillTriangle(64, -4, 58, 5, 70, 5);
  } else if (id === "flame") {
    g.fillStyle(0x7e2f23);
    g.fillTriangle(49, 33, 64, -5, 79, 33);
    g.fillStyle(style.accent);
    g.fillTriangle(55, 31, 64, 5, 73, 31);
    g.fillStyle(0xffec8c);
    g.fillTriangle(60, 28, 64, 14, 68, 28);
  } else {
    g.fillStyle(style.accent, 0.8);
    g.fillCircle(64, 15, 10);
    g.fillCircle(54, 24, 7);
    g.fillCircle(75, 25, 8);
    g.lineStyle(3, 0x375526, 1);
    g.strokeCircle(64, 19, radius + 5);
    if (level >= 3) {
      g.fillStyle(0xc7ea7c, 0.75);
      g.fillCircle(45, 7, 4);
      g.fillCircle(85, 9, 4);
    }
  }
}

function drawDefensiveWeapon(
  g: Phaser.GameObjects.Graphics,
  id: string,
  level: number,
  style: ProductionTowerStyle,
): void {
  if (id === "crossbow") {
    g.fillStyle(style.primary);
    g.fillRoundedRect(51, 21, 26, 11, 4);
    g.lineStyle(level >= 4 ? 5 : 4, style.accent, 1);
    g.lineBetween(35, 10, 64, 26);
    g.lineBetween(93, 10, 64, 26);
    g.lineStyle(2, style.secondary, 1);
    g.lineBetween(35, 10, 35, 42);
    g.lineBetween(93, 10, 93, 42);
    g.lineBetween(35, 42, 64, 26);
    g.lineBetween(93, 42, 64, 26);
    g.lineBetween(64, 26, 64, -1);
  } else if (id === "scout-tower") {
    g.fillStyle(style.primary);
    g.fillTriangle(32, 42, 64, 3, 96, 42);
    g.lineStyle(4, style.secondary, 1);
    g.strokeTriangle(32, 42, 64, 3, 96, 42);
    g.fillStyle(0xd9b28a);
    g.fillCircle(64, 24, 7);
    g.fillStyle(style.secondary);
    g.fillTriangle(56, 23, 64, 12, 72, 23);
    g.lineStyle(3, style.accent, 1);
    g.strokeCircle(78, 21, level >= 4 ? 10 : 8);
    g.lineBetween(84, 27, 92, 37);
  } else if (id === "sniper") {
    g.fillStyle(style.primary);
    g.fillRoundedRect(50, 24, 28, 12, 4);
    g.fillStyle(style.secondary);
    g.fillRect(61, -1, 7, 42);
    g.fillRect(43, 17, 42, 6);
    g.fillStyle(0x14191d);
    g.fillCircle(84, 20, 5);
    g.lineStyle(2, style.accent, 1);
    g.lineBetween(38, 17, 92, 17);
    g.lineBetween(37, 20, 93, 20);
  } else {
    g.fillStyle(style.secondary);
    g.fillRoundedRect(54, 5, 20, 41, 6);
    g.fillStyle(style.primary);
    g.fillTriangle(45, 11, 64, -5, 83, 11);
    g.lineStyle(4, style.accent, 1);
    g.lineBetween(64, 7, 64, 43);
    g.lineBetween(47, 25, 81, 25);
    g.fillStyle(0x202225);
    g.fillTriangle(48, 7, 56, 20, 40, 20);
    g.fillTriangle(80, 7, 88, 20, 72, 20);
  }
}

function drawEliteFocus(
  g: Phaser.GameObjects.Graphics,
  id: string,
  level: number,
  style: ProductionTowerStyle,
): void {
  if (id === "earthshaker") {
    g.fillStyle(style.secondary);
    g.fillRoundedRect(48, 19, 32, 28, 6);
    g.fillStyle(style.primary);
    g.fillCircle(64, 17, level >= 4 ? 18 : 14);
    g.lineStyle(4, style.accent, 1);
    g.lineBetween(42, 33, 64, 8);
    g.lineBetween(86, 33, 64, 8);
    g.fillStyle(style.accent, 0.7);
    g.fillTriangle(57, 12, 64, -4, 71, 12);
  } else if (id === "time") {
    g.fillStyle(style.secondary);
    g.fillCircle(64, 21, level >= 4 ? 23 : 19);
    g.lineStyle(4, style.accent, 1);
    g.strokeCircle(64, 21, level >= 4 ? 20 : 16);
    g.lineBetween(64, 21, 64, 8);
    g.lineBetween(64, 21, 76, 27);
    for (let i = 0; i < 8; i += 1) {
      const angle = (Math.PI * 2 * i) / 8;
      g.lineBetween(
        64 + Math.cos(angle) * 14,
        21 + Math.sin(angle) * 14,
        64 + Math.cos(angle) * 18,
        21 + Math.sin(angle) * 18,
      );
    }
  } else if (id === "alchemist") {
    g.fillStyle(style.secondary);
    g.fillRoundedRect(48, 9, 32, 38, 8);
    g.fillStyle(style.primary);
    g.fillCircle(55, 25, 10);
    g.fillCircle(73, 28, 12);
    g.fillStyle(style.accent, 0.9);
    g.fillCircle(54, 22, 5);
    g.fillCircle(75, 25, 6);
    g.lineStyle(3, 0xd8cba2, 1);
    g.lineBetween(52, 6, 52, 17);
    g.lineBetween(72, 4, 72, 15);
  } else if (id === "banner") {
    g.lineStyle(5, 0x4a321f, 1);
    g.lineBetween(64, -5, 64, 48);
    g.fillStyle(style.primary);
    g.fillTriangle(65, 0, 103, 12, 65, 31);
    g.lineStyle(3, style.accent, 1);
    g.lineBetween(66, 2, 99, 12);
    g.lineBetween(99, 12, 66, 28);
    g.fillStyle(style.accent);
    g.fillCircle(64, -4, 5);
  } else if (id === "arcane") {
    g.fillStyle(style.primary, 0.3);
    g.fillCircle(64, 20, level >= 4 ? 29 : 23);
    g.lineStyle(4, style.accent, 1);
    g.strokeCircle(64, 20, level >= 4 ? 22 : 18);
    g.fillStyle(style.accent, 0.9);
    g.fillTriangle(64, -5, 51, 18, 64, 42);
    g.fillTriangle(64, -5, 77, 18, 64, 42);
  } else if (id === "gold") {
    g.fillStyle(style.secondary);
    g.fillRoundedRect(44, 12, 40, 34, 7);
    g.fillStyle(style.accent);
    g.fillCircle(64, 20, level >= 4 ? 18 : 14);
    g.fillStyle(0x8f6727);
    g.fillCircle(64, 20, level >= 4 ? 11 : 8);
    g.lineStyle(3, style.accent, 1);
    g.lineBetween(45, 40, 83, 40);
    g.fillStyle(style.accent, 0.7);
    g.fillCircle(48, 5, 4);
    g.fillCircle(82, 7, 4);
  } else {
    g.fillStyle(style.secondary);
    g.fillTriangle(42, 39, 64, -3, 86, 39);
    g.fillStyle(style.primary);
    g.fillTriangle(39, 30, 10, 11, 45, 48);
    g.fillTriangle(89, 30, 118, 11, 83, 48);
    g.fillStyle(style.accent);
    g.fillCircle(57, 18, 4);
    g.fillCircle(71, 18, 4);
    if (level >= 4) {
      g.fillStyle(0xffd16a, 0.55);
      g.fillTriangle(58, 29, 64, 47, 70, 29);
    }
  }
}

function createProductionTower(
  scene: Phaser.Scene,
  id: string,
  level: number,
  style: ProductionTowerStyle,
): void {
  const g = scene.add.graphics();
  drawProductionBase(g, level, style);
  if (style.family === "artillery") drawArtilleryWeapon(g, id, level, style);
  else if (style.family === "arcane") drawArcaneFocus(g, id, level, style);
  else if (style.family === "defensive") drawDefensiveWeapon(g, id, level, style);
  else drawEliteFocus(g, id, level, style);

  if (level >= 3) {
    g.fillStyle(style.primary);
    g.fillTriangle(20, 58, 40, 63, 20, 80);
    g.fillStyle(style.accent);
    g.fillCircle(25, 66, 3);
  }
  if (level >= 5) {
    g.fillStyle(style.accent);
    g.fillCircle(34, 67, 4);
    g.fillCircle(94, 67, 4);
  }

  g.generateTexture(`tower-${id}-l${level}`, 128, 116);
  if (level === 1) g.generateTexture(`tower-${id}`, 128, 116);
  g.destroy();
}

function createTower(scene: Phaser.Scene, id: string, color: number, level: number): void {
  if (id === "archer") {
    createArcherTower(scene, level);
    return;
  }
  const productionStyle = PRODUCTION_TOWER_STYLES[id];
  if (productionStyle) {
    createProductionTower(scene, id, level, productionStyle);
    return;
  }

  const g = scene.add.graphics();
  const stone = level >= 5 ? 0xc5bda9 : level >= 3 ? 0xa99c86 : 0x8d806d;
  const darkStone = level >= 4 ? 0x4d4d4a : 0x554b40;
  const gold = level >= 4 ? 0xf0cf68 : 0xc5a24c;
  const roof =
    level >= 4
      ? Phaser.Display.Color.GetColor(
          Math.min(255, ((color >> 16) & 0xff) + 24),
          Math.min(255, ((color >> 8) & 0xff) + 24),
          Math.min(255, (color & 0xff) + 24),
        )
      : color;

  g.fillStyle(0x0a0908, 0.42).fillEllipse(64, 105, 102, 28);
  g.fillStyle(0x5a5145)
    .fillEllipse(64, 94, 96, 29)
    .lineStyle(4, 0x28231e)
    .strokeEllipse(64, 94, 96, 29);
  g.fillStyle(stone)
    .fillRoundedRect(25, 42, 78, 55, 7)
    .lineStyle(4, 0x3b332b)
    .strokeRoundedRect(25, 42, 78, 55, 7);

  // Masonry blocks and buttresses become richer each rank.
  g.lineStyle(2, darkStone, 0.55);
  for (let y = 51; y < 92; y += 13) {
    g.lineBetween(28, y, 100, y);
    for (let x = 28 + ((y / 13) % 2 ? 12 : 0); x < 100; x += 24) g.lineBetween(x, y - 9, x, y);
  }
  g.fillStyle(darkStone).fillRect(20, 56, 11, 42).fillRect(97, 56, 11, 42);
  g.fillStyle(0x17130f).fillRoundedRect(55, 72, 18, 25, 8);
  g.fillStyle(0xe6b953, 0.42).fillRoundedRect(59, 76, 10, 17, 5);

  if (level >= 2) {
    g.fillStyle(stone)
      .fillRoundedRect(31, 29, 66, 28, 5)
      .lineStyle(3, 0x3b332b)
      .strokeRoundedRect(31, 29, 66, 28, 5);
    for (let x = 32; x <= 88; x += 18) g.fillStyle(stone).fillRect(x, 22, 11, 14);
  }
  if (level >= 3) {
    g.fillStyle(gold).fillCircle(27, 64, 5).fillCircle(101, 64, 5);
    g.lineStyle(3, gold, 0.9).strokeCircle(64, 54, 9);
    g.fillStyle(0x243c55)
      .fillTriangle(18, 44, 31, 48, 18, 74)
      .fillTriangle(110, 44, 97, 48, 110, 74);
  }
  if (level >= 4) {
    g.fillStyle(stone).fillRoundedRect(12, 49, 18, 50, 6).fillRoundedRect(98, 49, 18, 50, 6);
    g.fillStyle(roof).fillTriangle(8, 51, 21, 27, 34, 51).fillTriangle(94, 51, 107, 27, 120, 51);
    g.lineStyle(3, 0x2f241c)
      .strokeTriangle(8, 51, 21, 27, 34, 51)
      .strokeTriangle(94, 51, 107, 27, 120, 51);
  }
  if (level >= 5) {
    g.fillStyle(gold, 0.22)
      .fillCircle(64, 40, 31)
      .lineStyle(3, gold, 0.82)
      .strokeCircle(64, 40, 27);
    for (let i = 0; i < 8; i += 1) {
      const a = (Math.PI * 2 * i) / 8;
      g.lineBetween(
        64 + Math.cos(a) * 29,
        40 + Math.sin(a) * 29,
        64 + Math.cos(a) * 36,
        40 + Math.sin(a) * 36,
      );
    }
  }

  g.fillStyle(roof)
    .fillTriangle(25, 44, 64, 6, 103, 44)
    .lineStyle(4, 0x33261d)
    .strokeTriangle(25, 44, 64, 6, 103, 44);

  // Weapon silhouettes remain distinct while gaining ornamentation.
  if (
    id.includes("archer") ||
    id.includes("crossbow") ||
    id.includes("ballista") ||
    id.includes("scout")
  ) {
    g.lineStyle(5, 0xe1c47e).strokeCircle(64, 28, 15).lineBetween(64, 12, 64, 45);
    g.lineStyle(3, 0x4b2d1e).lineBetween(43, 29, 85, 29);
    if (level >= 4) g.fillStyle(gold).fillTriangle(64, 4, 59, 12, 69, 12);
  } else if (id.includes("rocket") || id.includes("fire") || id.includes("flame")) {
    g.fillStyle(0x332b25).fillRoundedRect(51, 9, 26, 42, 6);
    g.fillStyle(0xd38136).fillTriangle(51, 10, 64, -2, 77, 10);
    g.fillStyle(level >= 3 ? 0xffd86b : 0xd9c179).fillRect(56, 20, 16, 16);
  } else if (id.includes("lightning") || id.includes("arcane") || id.includes("frost")) {
    g.fillStyle(level >= 4 ? 0xd2c6ff : 0x9b82c5, 0.82).fillCircle(64, 23, 18);
    g.lineStyle(5, level >= 3 ? 0xffed8a : 0xf2dd70)
      .lineBetween(69, 2, 53, 24)
      .lineBetween(53, 24, 75, 20)
      .lineBetween(75, 20, 58, 47);
  } else if (id.includes("dragon")) {
    g.fillStyle(0x6f2e2b)
      .fillTriangle(42, 38, 64, 2, 86, 38)
      .fillTriangle(36, 32, 11, 18, 43, 48)
      .fillTriangle(92, 32, 117, 18, 85, 48);
    g.fillStyle(gold).fillCircle(58, 21, 3).fillCircle(70, 21, 3);
  } else {
    g.fillStyle(0x25292b)
      .fillRoundedRect(56, 3, 18, 54, 6)
      .fillStyle(gold)
      .fillRect(51, 39, 28, 11)
      .fillStyle(0x16191b)
      .fillCircle(65, 7, 9);
  }

  g.fillStyle(gold).fillCircle(35, 66, 4).fillCircle(93, 66, 4);
  g.generateTexture(`tower-${id}-l${level}`, 128, 116);
  if (level === 1) g.generateTexture(`tower-${id}`, 128, 116);
  g.destroy();
}

function createEnemy(scene: Phaser.Scene, id: string, color: number): void {
  const g = scene.add.graphics();
  const heavy = ["knight", "guard", "siege", "shield-bearer", "crown-champion"].includes(id);
  const boss = ["siege", "war-mammoth", "crown-champion"].includes(id);
  const fast = ["scout", "gilded-raider", "shadow-runner", "winged-raider"].includes(id);
  const bodyWidth = boss ? 34 : heavy ? 27 : fast ? 19 : 22;
  const bodyHeight = boss ? 34 : 29;
  const skin = id === "frostbound" ? 0xc7dbe4 : 0xd9b58b;
  const leather = 0x5a3825;
  const darkLeather = 0x35251d;
  const cloth = color;
  const metal = heavy ? 0x778086 : 0x9b876a;
  const highlight = Phaser.Display.Color.GetColor(
    Math.min(255, ((color >> 16) & 0xff) + 34),
    Math.min(255, ((color >> 8) & 0xff) + 34),
    Math.min(255, (color & 0xff) + 34),
  );

  // Grounding shadow and lower body.
  g.fillStyle(0x090807, 0.34).fillEllipse(32, 57, boss ? 48 : 38, boss ? 13 : 10);
  g.fillStyle(darkLeather).fillRoundedRect(23, 44, 8, 15, 3).fillRoundedRect(35, 44, 8, 15, 3);
  g.fillStyle(0x201915).fillRect(21, 55, 11, 5).fillRect(34, 55, 11, 5);

  // Torso, belt, shoulders, and head establish the bandit faction silhouette.
  g.fillStyle(cloth).fillRoundedRect(32 - bodyWidth / 2, 21, bodyWidth, bodyHeight, 7);
  g.fillStyle(highlight, 0.5).fillRoundedRect(32 - bodyWidth / 2 + 3, 24, 5, bodyHeight - 8, 3);
  g.fillStyle(leather).fillRect(32 - bodyWidth / 2, 38, bodyWidth, 5);
  g.fillStyle(0xd2ae55).fillRect(29, 38, 6, 5);
  g.fillStyle(skin).fillCircle(32, 17, boss ? 10 : 8);
  g.fillStyle(darkLeather).fillRect(24, 10, 16, 7);
  g.fillStyle(color).fillTriangle(23, 13, 41, 13, 35, 7);
  g.fillStyle(0xf0d5ad).fillCircle(29, 17, 1.5).fillCircle(35, 17, 1.5);
  g.fillStyle(0x3a241c).fillRect(29, 22, 7, 2);

  // Arms and faction-standard red scarf / shoulder wrap.
  g.fillStyle(skin).fillRoundedRect(17, 27, 8, 18, 4).fillRoundedRect(39, 27, 8, 18, 4);
  g.fillStyle(0x8e2f2f).fillTriangle(23, 23, 41, 23, 36, 31);

  if (id === "footman" || id === "militia" || id === "berserker") {
    g.fillStyle(0x765033).fillCircle(19, 35, id === "militia" ? 7 : 10);
    g.lineStyle(2, 0xc5a86d).strokeCircle(19, 35, id === "militia" ? 7 : 10);
    g.lineStyle(id === "berserker" ? 5 : 4, 0x4b3325).lineBetween(45, 28, 53, 52);
    g.fillStyle(0xb7bdc0).fillTriangle(48, 27, 55, 20, 53, 32);
    if (id === "berserker") {
      g.fillStyle(0xa8382d).fillTriangle(24, 10, 18, 3, 30, 9).fillTriangle(40, 10, 46, 3, 34, 9);
    }
  } else if (id === "scout" || id === "shadow-runner") {
    g.fillStyle(id === "shadow-runner" ? 0x30283b : 0x455f3e).fillTriangle(19, 25, 32, 4, 45, 25);
    g.lineStyle(3, leather).lineBetween(43, 24, 51, 53);
    g.lineStyle(2, 0xcbb579).strokeCircle(46, 31, 7).lineBetween(46, 24, 46, 38);
  } else if (id === "gilded-raider") {
    g.fillStyle(0xe4bd4a).fillTriangle(22, 11, 32, -1, 42, 11);
    g.lineStyle(3, 0xf4d97a).strokeCircle(32, 31, 13);
    g.fillStyle(0x6b4728).fillRoundedRect(45, 23, 7, 32, 3);
    g.fillStyle(0xe4bd4a).fillTriangle(48, 20, 43, 29, 53, 29);
  } else if (id === "knight" || id === "guard" || id === "shield-bearer") {
    g.fillStyle(metal).fillRoundedRect(13, 24, 17, 29, 8);
    g.lineStyle(2, 0xd3d7d5).strokeRoundedRect(13, 24, 17, 29, 8);
    g.fillStyle(0x45515a).fillRect(25, 8, 14, 6);
    g.lineStyle(4, 0x4a3325).lineBetween(46, 24, 52, 54);
    g.fillStyle(0xc4c9ca).fillTriangle(49, 20, 44, 30, 54, 30);
    if (id === "guard") {
      g.fillStyle(0xe0bd54).fillTriangle(28, 10, 32, -1, 36, 10).fillRect(29, 28, 7, 20);
    }
    if (id === "shield-bearer") {
      g.fillStyle(0x577080).fillRoundedRect(11, 22, 20, 34, 9);
      g.lineStyle(3, 0xb8c4c8).strokeRoundedRect(11, 22, 20, 34, 9);
      g.fillStyle(0xd1b25e).fillCircle(21, 39, 4);
    }
  } else if (id === "standard-bearer") {
    g.lineStyle(4, 0x503522).lineBetween(47, 6, 47, 58);
    g.fillStyle(0x8e2f2f).fillTriangle(48, 8, 61, 16, 48, 26);
    g.lineStyle(2, 0xd9b95f).lineBetween(48, 8, 61, 16).lineBetween(61, 16, 48, 26);
  } else if (id === "siege") {
    g.fillStyle(0x6f5435).fillRoundedRect(8, 20, 17, 37, 5).fillRoundedRect(39, 20, 17, 37, 5);
    g.lineStyle(5, 0x2d2118).lineBetween(8, 39, 56, 39);
    g.fillStyle(0xa5783c).fillRect(27, 4, 10, 50);
  } else if (id === "winged-raider") {
    g.fillStyle(0x655046, 0.92)
      .fillTriangle(24, 28, 5, 15, 20, 42)
      .fillTriangle(40, 28, 59, 15, 44, 42);
  } else if (id === "war-mammoth") {
    g.fillStyle(0x66594d).fillEllipse(32, 31, 48, 32);
    g.fillStyle(0xcabda3).fillTriangle(15, 32, 4, 45, 19, 39).fillTriangle(49, 32, 60, 45, 45, 39);
    g.fillStyle(0x51463d).fillRect(15, 42, 8, 17).fillRect(41, 42, 8, 17);
  } else if (id === "crown-champion") {
    g.fillStyle(0xe0bd54).fillTriangle(22, 10, 27, -1, 32, 9).fillTriangle(32, 9, 37, -1, 42, 10);
    g.fillStyle(0x8f2735).fillRoundedRect(13, 24, 18, 30, 8);
    g.lineStyle(3, 0xf1d173).strokeRoundedRect(13, 24, 18, 30, 8);
    g.fillStyle(0xe0bd54).fillCircle(32, 34, 6);
  } else {
    // Specialist enemies retain their elemental identity while sharing the bandit anatomy.
    g.fillStyle(highlight, 0.7).fillCircle(19, 34, 8);
    g.lineStyle(3, color, 0.9).strokeCircle(19, 34, 8);
    g.fillStyle(color, 0.5).fillCircle(47, 30, 5);
  }

  g.generateTexture(`enemy-${id}`, 64, 64);
  g.destroy();
}

function createEffects(scene: Phaser.Scene): void {
  const arrow = scene.add.graphics();
  arrow
    .lineStyle(3, 0xe5d4a0)
    .lineBetween(4, 8, 28, 8)
    .fillStyle(0xc5c8c2)
    .fillTriangle(28, 3, 36, 8, 28, 13)
    .fillStyle(0x8b3d2e)
    .fillTriangle(6, 8, 0, 3, 0, 13);
  arrow.generateTexture("projectile-arrow", 38, 16);
  arrow.destroy();
  const rocket = scene.add.graphics();
  rocket
    .fillStyle(0xb9452c)
    .fillRoundedRect(5, 5, 28, 12, 6)
    .fillStyle(0xe7c25b)
    .fillTriangle(33, 5, 43, 11, 33, 17)
    .fillStyle(0xf4a23a)
    .fillTriangle(5, 6, 0, 11, 5, 16);
  rocket.generateTexture("projectile-rocket", 44, 22);
  rocket.destroy();
  const ball = scene.add.graphics();
  ball.fillStyle(0x1d2225).fillCircle(10, 10, 8).fillStyle(0x929b9c, 0.65).fillCircle(7, 7, 3);
  ball.generateTexture("projectile-cannon", 20, 20);
  ball.destroy();
  const spark = scene.add.graphics();
  spark.fillStyle(0xffe28a, 0.85).fillCircle(20, 20, 7).lineStyle(3, 0xf5bb43, 0.75);
  for (let i = 0; i < 8; i += 1) {
    const a = (i * Math.PI) / 4;
    spark.lineBetween(
      20 + Math.cos(a) * 9,
      20 + Math.sin(a) * 9,
      20 + Math.cos(a) * 19,
      20 + Math.sin(a) * 19,
    );
  }
  spark.generateTexture("effect-impact", 40, 40);
  spark.destroy();
  const dust = scene.add.graphics();
  dust
    .fillStyle(0xb89a68, 0.22)
    .fillCircle(20, 22, 15)
    .fillStyle(0xd0b47d, 0.35)
    .fillCircle(12, 16, 8)
    .fillCircle(28, 14, 7);
  dust.generateTexture("effect-dust", 42, 42);
  dust.destroy();
}

export function createProceduralTextures(scene: Phaser.Scene): void {
  for (const [id, color] of Object.entries(TOWER_COLORS))
    for (let level = 1; level <= 5; level += 1) createTower(scene, id, color, level);
  for (const [id, color] of Object.entries(ENEMY_COLORS)) createEnemy(scene, id, color);
  createEffects(scene);
  const crest = scene.add.graphics();
  crest
    .fillStyle(0x15191f)
    .fillCircle(32, 32, 30)
    .lineStyle(5, 0xf4c95d)
    .strokeCircle(32, 32, 27)
    .lineStyle(2, 0x8f6a25)
    .strokeCircle(32, 32, 22);
  crest
    .fillStyle(0xf4c95d)
    .fillRect(20, 23, 24, 23)
    .fillRect(16, 16, 8, 12)
    .fillRect(28, 16, 8, 12)
    .fillRect(40, 16, 8, 12);
  crest.generateTexture("ui-crest", 64, 64);
  crest.destroy();
}
